/*
 * Copyright (c) 2024, King Living
 * Resolve Contentful Link entry URLs to PWA app paths.
 */

import {urlHelper} from '@amblique/base/app/helpers/url-helper';

const SCHEME_PREFIX = /^([a-z][a-z\d+.-]*):/i;
const BLOCKED_SCHEMES = new Set(['javascript', 'data', 'vbscript']);

const EMPTY_TARGET = {
  displayUrl: '',
  href: undefined,
  isHashOnly: false,
  linkUrl: null,
  to: undefined,
};

/** @param {string|null|undefined} url @returns {string} */
export function normalizeContentfulUrl(url) {
  return typeof url === 'string' ? url.trim() : '';
}

/** RFC 3986 scheme on an already-trimmed URL. Empty for paths, `#hash`, and `//host`. */
function getUrlScheme(normalized) {
  const match = normalized.match(SCHEME_PREFIX);

  return match ? match[1].toLowerCase() : '';
}

/** Executable schemes — must not become `href` or SPA `to`. */
export function isBlockedScriptUrl(url) {
  return BLOCKED_SCHEMES.has(getUrlScheme(normalizeContentfulUrl(url)));
}

/** Native `<a href>` (not React Router / `buildUrl`): any scheme except blocked, or `//host`. */
export function isNativeOrAbsoluteUrl(url) {
  const normalized = normalizeContentfulUrl(url);

  if (!normalized) {
    return false;
  }

  const scheme = getUrlScheme(normalized);

  if (BLOCKED_SCHEMES.has(scheme)) {
    return false;
  }

  return normalized.startsWith('//') || Boolean(scheme);
}

/**
 * Overlay Link: `false` = blocked (render span), string = native href, `null` = SPA parent.
 *
 * @param {string|null|undefined} to
 * @param {string|null|undefined} href
 * @param {boolean} [isExternal]
 * @returns {false|string|null}
 */
export function resolveNativeAnchorHref(to, href, isExternal) {
  if (isBlockedScriptUrl(to) || isBlockedScriptUrl(href)) {
    return false;
  }

  const candidate = to ?? href;

  if (isNativeOrAbsoluteUrl(candidate)) {
    return normalizeContentfulUrl(candidate);
  }

  return isExternal && href ? href : null;
}

/**
 * Hash (`#…`) and native/app URLs → `href`; SFRA/internal paths → `to`.
 * Blocked scripts → empty (no link).
 *
 * @param {string|null|undefined} url
 * @returns {{displayUrl: string, href?: string, isHashOnly: boolean, linkUrl: string|null, to?: string}}
 */
export function resolveContentfulLinkTargetProps(url) {
  const displayUrl = normalizeContentfulUrl(url);

  if (!displayUrl || BLOCKED_SCHEMES.has(getUrlScheme(displayUrl))) {
    return EMPTY_TARGET;
  }

  const isHashOnly = displayUrl.startsWith('#');
  const useHref = isHashOnly || displayUrl.startsWith('//') || Boolean(getUrlScheme(displayUrl));
  const parsed = useHref ? null : urlHelper.parseStorefrontUrl(displayUrl);
  const linkUrl = parsed?.appUrl || displayUrl;

  return {
    displayUrl,
    href: useHref ? linkUrl : undefined,
    isHashOnly,
    linkUrl,
    to: useHref ? undefined : linkUrl,
  };
}

/** App path or native URL from a Contentful Link entry (`link.url`). */
export function resolveContentfulLinkUrl(link) {
  return resolveContentfulLinkTargetProps(link?.url).linkUrl;
}
